import H2 from '@base/H2/H2';
import Dialog from '@comp/Dialog/Dialog';
import { Spacings } from '@styles/index';
import styled from '@emotion/styled';
import React, { useEffect, useState } from 'react';
import P1 from '@base/P1/P1';
import Button from '@base/Button/Button';
import PlusIcon from '@icon/Syllabus/PlusIcon';
import MinusIcon from '@icon/Syllabus/MinusIcon';
import { setPracticeQuestionCount } from '@actions/practiceSession';
import useConceptSelector from '@cont/Practice/Listing/ConceptSelectorDialog/useConceptSelector';
import { useSelector, useDispatch } from 'react-redux';
import { TOPIC_SYLLABUS_PAGE } from '@constants/segment';
import { useRouter } from 'next/router';
import { PRACTICE_FLOW_TYPE } from '@cont/Practice/constants';
import { getIsSubscribedToGoal } from '@selectors/subscription';

interface IStartPracticeDialogProps {
  dialogState: { isOpen: boolean; title: string };
  onDialogClose: () => void;
}

const maxNoOfQuestions = 30;
const minNoOfQuestions = 5;
const defaultNoOfQuestions = 10;
const noOfFreeQuestions = 5;
const stepper = 5;

const StyH2 = styled(H2)`
  color: var(color-text-primary);
  text-align: center;
  margin-bottom: ${Spacings.SPACING_2B};
`;

const Flex = styled.div`
  display: flex;
  align-items: center;
  margin: ${Spacings.SPACING_8B} 0;
`;

const Container = styled.div`
  width: 560px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${Spacings.SPACING_10B} ${Spacings.SPACING_12B};
`;

const MinusButton = styled.div`
  background-color: transparent;
  border-radius: ${Spacings.SPACING_2B};
  border: ${Spacings.SPACING_1} solid var(--color-divider);
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${Spacings.SPACING_14B};
  height: ${Spacings.SPACING_14B};
  cursor: ${({
    // @ts-ignore
    $disable
  }) => ($disable ? 'not-allowed' : 'pointer')};
  :hover {
    background-color: ${({
      // @ts-ignore
      $disable
    }) => ($disable ? 'transparent' : 'var(--color-divider)')};
  }
`;

const PlusButton = styled.div`
  background-color: transparent;
  border-radius: ${Spacings.SPACING_2B};
  border: 1px solid var(--color-divider);
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${Spacings.SPACING_14B};
  height: ${Spacings.SPACING_14B};
  cursor: ${({
    // @ts-ignore
    $disable
  }) => ($disable ? 'not-allowed' : 'pointer')};
  :hover {
    background-color: ${({
      // @ts-ignore
      $disable
    }) => ($disable ? 'transparent' : 'var(--color-divider)')};
  }
`;

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: center;
  button {
    &:not(:first-child) {
      margin-left: ${Spacings.SPACING_4B};
    }
  }
`;

const StartPracticeButton = styled(Button)`
  background-color: var(--color-text-primary);
  color: var(--color-text-tertiary);
`;

const CancelButton = styled(Button)`
  background-color: var(--color-base-1);
`;

const TextWrapper = styled.div`
  width: 133px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StartPracticeDialog: React.FC<IStartPracticeDialogProps> = ({
  dialogState,
  onDialogClose
}) => {
  const {
    query: { goalUID, topicUid: topicGroupUID }
  } = useRouter();
  const isSubscribed = useSelector(getIsSubscribedToGoal(goalUID));
  const [noOfQuestions, setNoOfQuestions] = useState(
    isSubscribed ? defaultNoOfQuestions : noOfFreeQuestions
  );
  const dispatch = useDispatch();
  const { handleStartPractice } = useConceptSelector({
    topicGroupUID,
    flowType: PRACTICE_FLOW_TYPE.SYLLABUS_PRACTICE,
    lastPrimarySource: TOPIC_SYLLABUS_PAGE
  });
  const plusButtonClick = () => {
    if (noOfQuestions < maxNoOfQuestions) {
      setNoOfQuestions(noOfQuestions + stepper);
      dispatch(setPracticeQuestionCount(noOfQuestions + stepper));
    }
  };
  const MinusButtonClick = () => {
    if (noOfQuestions > minNoOfQuestions) {
      setNoOfQuestions(noOfQuestions - stepper);
      dispatch(setPracticeQuestionCount(noOfQuestions - stepper));
    }
  };
  const isPlusButtonDisabled =
    noOfQuestions === maxNoOfQuestions || !isSubscribed;
  const isMinusButtonDisabled = noOfQuestions === minNoOfQuestions;

  const onStartPracticeClick = () => {
    handleStartPractice();
    onDialogClose();
  };

  useEffect(() => {
    dispatch(setPracticeQuestionCount(defaultNoOfQuestions));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog
      dialogState={dialogState.isOpen}
      backgroundColor="var(--color-base-0)"
      borderRadius={Spacings.SPACING_2B}
      onClose={onDialogClose}
    >
      <Container>
        <StyH2>{dialogState.title}</StyH2>
        <P1 color="var(--color-text-secondary)">
          How many questions do you want to practice?
        </P1>
        <Flex>
          <MinusButton
            // @ts-ignore
            $disable={isMinusButtonDisabled}
            onClick={MinusButtonClick}
          >
            <MinusIcon
              size="24px"
              color={
                isMinusButtonDisabled
                  ? 'var(--color-text-secondary)'
                  : 'var(--color-text-primary)'
              }
            />
          </MinusButton>
          <TextWrapper>
            <H2>{noOfQuestions}</H2>
          </TextWrapper>
          <PlusButton
            onClick={plusButtonClick}
            // @ts-ignore
            $disable={isPlusButtonDisabled}
          >
            <PlusIcon
              size="24px"
              color={
                isPlusButtonDisabled
                  ? 'var(--color-text-secondary)'
                  : 'var(--color-text-primary)'
              }
            />
          </PlusButton>
        </Flex>
        <ButtonsWrapper>
          <CancelButton
            // @ts-ignore
            label="Cancel"
            size="small"
            variant="hollow"
            color="var(--color-text-primary)"
            onClick={onDialogClose}
          />
          <StartPracticeButton
            // @ts-ignore
            label="Start practice"
            size="small"
            variant="filled"
            onClick={onStartPracticeClick}
          />
        </ButtonsWrapper>
      </Container>
    </Dialog>
  );
};

export default StartPracticeDialog;
